import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { db, type Record, type Event, type RecordInsert, type EventInsert } from '../../lib/db';
import { useSession } from '../../auth/useSession';
import { sha256 } from './hash';

interface UseRecordsParams {
  page?: number;
  pageSize?: number;
  q?: string;
}

interface RecordsResponse {
  data: Record[];
  count: number;
  totalPages: number;
}

export function useRecords({ page = 1, pageSize = 20, q }: UseRecordsParams = {}) {
  const { user } = useSession();

  return useQuery({
    queryKey: ['records', { page, pageSize, q, userId: user?.id }],
    queryFn: async (): Promise<RecordsResponse> => {
      if (!user) throw new Error('User not authenticated');

      let query = db
        .from('records')
        .select('*', { count: 'exact' })
        .eq('owner', user.id)
        .order('created_at', { ascending: false });

      // Search functionality
      if (q) {
        query = query.or(`title.ilike.%${q}%,description.ilike.%${q}%`);
      }

      // Pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        count: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
    enabled: !!user,
  });
}

export function useRecord(id: string) {
  const { user } = useSession();

  const recordQuery = useQuery({
    queryKey: ['record', id],
    queryFn: async (): Promise<Record> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await db
        .from('records')
        .select('*')
        .eq('id', id)
        .eq('owner', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user && !!id,
  });

  const eventsQuery = useQuery({
    queryKey: ['record-events', id],
    queryFn: async (): Promise<Event[]> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await db
        .from('events')
        .select('*')
        .eq('record_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user && !!id,
  });

  return {
    record: recordQuery.data,
    events: eventsQuery.data || [],
    isLoading: recordQuery.isLoading || eventsQuery.isLoading,
    error: recordQuery.error || eventsQuery.error,
  };
}

interface CreateRecordData {
  title: string;
  description?: string;
  content: string;
  tags?: string[];
}

export function useCreateRecord() {
  const { user } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRecordData): Promise<Record> => {
      if (!user) throw new Error('User not authenticated');

      // Generate content hash
      const content_hash = await sha256(data.content);

      // Check for duplicate content
      const { data: existingRecord } = await db
        .from('records')
        .select('id, title')
        .eq('content_hash', content_hash)
        .eq('owner', user.id)
        .single();

      if (existingRecord) {
        throw new Error(`Content already notarized in record: ${existingRecord.title}`);
      }

      // Create record
      const recordData: RecordInsert = {
        owner: user.id,
        title: data.title,
        description: data.description,
        content_hash,
        tags: data.tags || [],
        status: 'draft',
      };

      const { data: record, error: recordError } = await db
        .from('records')
        .insert(recordData)
        .select()
        .single();

      if (recordError) throw recordError;

      // Create initial event
      const eventData: EventInsert = {
        record_id: record.id,
        actor: user.id,
        type: 'created',
        data: {
          title: data.title,
          content_hash,
        },
      };

      const { error: eventError } = await db
        .from('events')
        .insert(eventData);

      if (eventError) {
        console.error('Failed to create event:', eventError);
        // Don't throw here - record creation succeeded
      }

      return record;
    },
    onSuccess: () => {
      // Invalidate and refetch records
      queryClient.invalidateQueries({ queryKey: ['records'] });
    },
  });
}

export function useUpdateRecordStatus() {
  const { user } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      recordId, 
      status, 
      data: eventData 
    }: { 
      recordId: string; 
      status: Record['status']; 
      data?: any 
    }): Promise<void> => {
      if (!user) throw new Error('User not authenticated');

      // Update record status
      const { error: updateError } = await db
        .from('records')
        .update({ status })
        .eq('id', recordId)
        .eq('owner', user.id);

      if (updateError) throw updateError;

      // Create status change event
      const event: EventInsert = {
        record_id: recordId,
        actor: user.id,
        type: status === 'submitted' ? 'submitted' : 
              status === 'verified' ? 'verified' : 
              status === 'rejected' ? 'rejected' : 'updated',
        data: eventData || { status },
      };

      const { error: eventError } = await db
        .from('events')
        .insert(event);

      if (eventError) {
        console.error('Failed to create status event:', eventError);
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['records'] });
      queryClient.invalidateQueries({ queryKey: ['record', variables.recordId] });
      queryClient.invalidateQueries({ queryKey: ['record-events', variables.recordId] });
    },
  });
}